import { Component, Inject, Input, OnChanges, OnDestroy, OnInit, Optional, SimpleChanges } from '@angular/core';
import { Grid } from '../../../core/components/grid/grid.model';
import { GridOptions } from '../../../core/components/grid/grid.types';
import { ModalService } from '../../../core/services/modal.service';
import { AcSuppTransGet, AcSuppTransService, GetAcSuppTransParams } from '@app/services/ac-supp-trans.service';
import { catchError, EMPTY, from, ReplaySubject, Subject, switchMap, takeUntil } from 'rxjs';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';

@Component({
    selector: 'tt-ac-supp-trans',
    templateUrl: './ac-supp-trans.component.html',
    styleUrls: ['./ac-supp-trans.component.css'],
})
export class AcSuppTransComponent implements OnInit, OnChanges, OnDestroy {
    @Input()
    public params?: GetAcSuppTransParams;

    public params$ = new ReplaySubject<GetAcSuppTransParams>(1);

    public destroy$ = new Subject<void>();

    public setting?: AcSuppTransGet;

    public grid?: GridOptions;

    public removing = false;

    constructor(
        private acSuppTrans: AcSuppTransService,
        private modal: ModalService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        this.navigator = useNavigatorFor(navigator, this);
        this.params$
            .pipe(
                takeUntil(this.destroy$),
                switchMap((params) => from(this.acSuppTrans.getAcSuppTrans(params)).pipe(catchError((err) => EMPTY))),
            )
            .subscribe({
                next: (get) => {
                    this.setting = get;
                    this.navigator?.setHeading?.(this.setting.heading_matching);
                    this.loadGrid();
                },
            });
    }

    private loadGrid() {
        if (!this.setting) return;

        if (!this.grid) {
            this.grid = new Grid()
                .setRememberId('w_acsupptrans_match_grid')
                .setLoadDataFunction({ method: 2829, parameters: () => this.setting })
                .setOptionFunc((data: any) => this.optionFunc(data.data));
        } else {
            this.grid?.gridfunc?.read();
        }
    }

    private optionFunc(data: any) {
        if (data.func && data.func === 'CheckboxBoxClick') {
            if (data.dataItem.is_selected === true) {
                data.dataItem.is_selected_amount_locval_balance = data.dataItem.amount_locval;
            } else {
                data.dataItem.is_selected_amount_locval_balance = 0;
            }

            this.grid?.gridfunc?.refresh();
            this.grid?.gridfunc?.refreshAggregates();
        } else if (data.func && data.func === 'CheckboxHeaderClick') {
            data.data.forEach((dataItem: any) => {
                if (dataItem.is_selected === true) {
                    dataItem.is_selected_amount_locval_balance = dataItem.amount_locval;
                } else {
                    dataItem.is_selected_amount_locval_balance = 0;
                }
            });

            this.grid?.gridfunc?.refresh();
            this.grid?.gridfunc?.refreshAggregates();
        }
    }

    public async removeChanges() {
        if (!!this.setting) {
            try {
                this.removing = true;

                const { errorcode, errormessage } = await this.acSuppTrans.removeAcSuppTransMatching({ ...this.setting, records: this.grid?.gridfunc?.getIsSelectedRows().map((row) => ({ acsupptransmatch_keyno: row['acsupptransmatch_keyno'] })) ?? [] });

                if ('' + errorcode !== '0') {
                    this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
                } else {
                    this.navigator?.back();
                }
            } catch (error) {
                console.error(error);
            } finally {
                this.removing = false;
            }
        }
    }

    private async init(params: GetAcSuppTransParams) {
        this.params = params;
        this.params$.next(this.params);
    }

    ngOnInit() {
        if (!!this.navigator) {
            this.navigator.setSize?.({ width: '95vw', height: '98vh' });
            this.init(this.navigator.params as any);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!!changes['params']?.currentValue) {
            this.init(changes['params'].currentValue);
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.params$.complete();
    }
}
